import { AfterContentInit, Component, Input, Output, EventEmitter } from "@angular/core";
import { Subject } from "rxjs";
import { LoggerService } from "@bfx/logger";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import {
  AutoHedgingChangeStateRequestImpl,
  AutoHedgingStatus,
  AutoHedgingStatusImpl,
  ErrorsImpl,
  StaticDataProfileImpl,
} from "shared/cubiq-model-multi-asset";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxBlotterOrdersActionService } from "app/bfx-blotter-orders/bfx-blotter-orders-action.service";
import { BfxAutoHedgeStatusChangeComponent } from "app/bfx-dialogs/bfx-dialog-auto-hedge-status-change.component";
import { BfxBmarkHedgeOrdersBlotterActionsService } from "app/bfx-bmark-summary/blotters/hedge-orders/services/bmark-hedge-orders-actions.service";
import { BfxHedgeOrderMonitorComponent } from "app/bfx-hedge-order-monitor/bfx-hedge-order-monitor.component";
import {
  BenchmarkDataPrimaryLookupService,
  CubiqWorker,
  NotificationService,
} from "shared/cubiq-services";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "auto-hedge-status",
  template: `
    <button
      type="button"
      [class]="{
        'autohedge-status': true,
        'bfx-button-sm': true,
        'bfx-button-positive-moderate-unsized': on,
        'bfx-button-negative-moderate-unsized': !on,
      }"
      [disabled]="disabled"
      (click)="onClick()"
    >
      {{ label }}
    </button>
  `,
  standalone: false,
})
export class AutoHedgeStatusComponent implements AfterContentInit {
  @Input() on: boolean;
  @Input() disabled: boolean;
  @Output() onChange: EventEmitter<AutoHedgingStatus> = new EventEmitter();

  label: string;

  constructor(
    protected _service: BfxBmarkHedgeOrdersBlotterActionsService,
    protected cubiqWorker: CubiqWorker,
    protected benchmarkDataService: BenchmarkDataPrimaryLookupService,
    protected notificationService: NotificationService,
  ) {}

  ngAfterContentInit(): void {
    logger.out.info(`initialise`);
    this.primeHedgingStatus();
  }

  private primeHedgingStatus() {
    logger.out.info(`HedgingStatusChange Prime`);

    this.subscribeForAutoHedgingStatusUpdates();

    this.cubiqWorker
      .getBroker()
      .primeStaticData<StaticDataProfileImpl>(StaticDataProfileImpl.name)
      .subscribe((profile: StaticDataProfileImpl) => {
        logger.out.info(`HedgingStatusChange Prime result`);
        this.setAutoHedgingStatus(profile.autoHedgingStatus.autoHedgingEnabled);
      });
  }

  private subscribeForAutoHedgingStatusUpdates() {
    this.benchmarkDataService.autoHedgeSubject.subscribe((response) => {
      logger.out.info(`HedgingStatusChange Update ${response.status.autoHedgingEnabled}`);
      this.setAutoHedgingStatus(response.status.autoHedgingEnabled);
    });
  }

  private setAutoHedgingStatus(autoHedgingEnabled: boolean): void {
    logger.out.info(`setAutoHedgingStatus ${autoHedgingEnabled}`);
    this.on = autoHedgingEnabled;
    this.update();
  }

  update() {
    if (true == this.on) {
      this.label = "BM Auto Hedging: ON";
    } else {
      this.label = "BM Auto Hedging: OFF";
    }
  }

  onClick() {
    logger.out.info(`onClick ${this.on}`);
    this.doAutoHedgeStatusChangeDialog();
  }

  doAutoHedgeStatusChangeDialog() {
    let payload = {
      callback: this.doAutoHedgeStatusChangeDialog_confirm.bind(this),
      currentState: this.on,
    };
    let dialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.AUTO_HEDGE_STATUS_CHANGE,
      null,
      null,
      payload,
    );
    BfxHedgeOrderMonitorComponent.addDialog(dialogData);
  }

  public doAutoHedgeStatusChangeDialog_confirm(actionEvent, comment) {
    logger.out.info(`doAutoHedgeStatusChangeDialog_confirm ${comment} `);
    let status: AutoHedgingStatus = new AutoHedgingStatusImpl();
    status.autoHedgingEnabled = !this.on; // flip it (we wait for a notif to change our state)
    status.comment = comment;
    this.doHedgingStatusChange(status);
    this.onChange.emit(status);
  }

  /**
   * @See onChange in bmark-summary-container.html
   */
  public doHedgingStatusChange(autoHedgingStatus: AutoHedgingStatus) {
    logger.out.info(`doHedgingStatusChange`);
    this.pushAutoHedgingStatus(autoHedgingStatus);
  }

  private pushAutoHedgingStatus(autoHedgingStatus: AutoHedgingStatus): void {
    logger.out.info(`pushAutoHedgingStatus ${autoHedgingStatus}`);

    let request: AutoHedgingChangeStateRequestImpl = new AutoHedgingChangeStateRequestImpl();
    request.autoHedgingEnabled = autoHedgingStatus.autoHedgingEnabled;
    request.comment = autoHedgingStatus.comment;

    this.cubiqWorker.postServiceRequest(request).then((value) => {
      if (value instanceof ErrorsImpl) {
        logger.out.info(`pushAutoHedgingStatus Error result ${value}`);
        this.onAutoHedgingStatusPushFail(value);
      }
    });
  }

  private onAutoHedgingStatusPushFail(value: ErrorsImpl) {
    this.notificationService.handleGenericError(value);
  }
}
